import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Step,
	StepLabel,
	Stepper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { FC, useEffect, useState } from "react";
import InputMask from 'react-input-mask';
import { useParams } from "react-router-dom";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import PrivacyPolicyLink from "../../components/privacy-policy-link/PrivacyPolicyLink";
import roupas1 from '../../images/roupas1.png';
import roupas2 from '../../images/roupas2.png';
import sapato1 from '../../images/sapato1.png';
import sapato2 from '../../images/sapato2.png';
import { AuditService } from "../../services/AuditService";
import { AuditAdmissionDTO } from "../../types/AuditAdmissionDTO";
import { AuditDocumentDTO } from '../../types/AuditDocumentDTO';
import { getLocation } from '../audit-admission/AuditAdmission';
import "./AuditAdmissionForm.scss";

const steps = ["Dados pessoais", "Documentos", "Informações adicionais", "Revisão"];

export const documentList = [
	"Carteira de trabalho (frente e verso)",
	"Identidade (RG)",
	"CPF",
	"Título de eleitor",
	"Comprovante de residência",
	"Foto 3X4",
	"Certidão de nascimento dos filhos menores de 14 anos",
	"Carteira de vascinação dos filhos menores de 6 anos",
	"Atestado de frequência escolar dos dependentes",
	"CPF dos dependentes",
	"Certidão de casamento do cônjugue",
	"Documento do cônjugue"
];

const raceOptions = ["Branco(a)", "Negro(a)", "Pardo(a)", "Amarelo(a)", "Indígena", "Não declarado(a)"];
const educationOptions = ["Analfabeto", "Ensino Fundamental Incompleto", "Ensino Fundamental Completo", "Ensino Médio Incompleto", "Ensino Médio Completo", "Ensino Superior Incompleto", "Ensino Superior Completo", "Pós-graduação"];
const maritalStatusOptions = ["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)", "União Estável"];
const clotheSizeOptions = ["PP", "P", "M", "G", "GG", "XG"];
const kinshipOptions = ["Cônjuge", "Pai/Mãe", "Filho(a)", "Irmão(ã)", "Outro"];

export const LGPDTerm = (
	<>
		<Typography variant="h6" gutterBottom>
			Termo de Consentimento LGPD
		</Typography>
		<Typography variant="caption" paragraph>
			Ao prosseguir, você declara que está ciente com o tratamento de seus dados pessoais para fins de processo seletivoe admissional, conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)
		</Typography>
	</>
)

const AuditAdmissionForm: FC = () => {
	const { id } = useParams();
	const [admission, setAdmission] = useState<AuditAdmissionDTO>({} as AuditAdmissionDTO);
	const [finalConsent, setFinalConsent] = useState(false);
	const [filesToUpload, setFilesToUpload] = useState<{ [key: string]: File }>({});
	const [terms, setTerms] = useState(false);
	const [showShoeSizeHelper, setShowShoeSizeHelper] = useState(false);
	const [shoeCm, setShoeCm] = useState<string>("");
	const [calculatedShoeSize, setCalculatedShoeSize] = useState<string>("");
	const [showClotheSizeHelper, setShowClotheSizeHelper] = useState(false);
	const [clotheCm, setClotheCm] = useState<string>("");
	const [calculatedClotheSize, setCalculatedClotheSize] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [requiredDocuments, setRequiredDocuments] = useState<string[]>([]);
	const service = new AuditService();
	const { showError, showAlert } = useAlert();

	const missingRequiredDocuments = () => requiredDocuments
		.filter(docName => !admission.documents?.some((doc: any) => doc.name === docName));

	const handleChange = (change: keyof AuditAdmissionDTO, value: any) => {
		setAdmission((prevAdmission) => ({
			...prevAdmission,
			[change]: value,
		}));
	}

	const handleFileSelect = (documentType: string, file: File) => {
		const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
		if (!acceptedTypes.includes(file.type)) {
			showError("Tipo de arquivo inválido. Apenas imagens e arquivos PDF são aceitos.");
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			showError("O arquivo excede o tamanho máximo de 5MB.");
			return;
		}

		setFilesToUpload(prev => ({
			...prev,
			[documentType]: file
		}));

		const newDocument = {
			name: documentType,
			document: file.name,
			originalName: file.name
		} as AuditDocumentDTO;

		setAdmission(prev => {
			const otherDocuments = prev.documents?.filter((d: any) => d.documentType !== documentType) || [];
			return {
				...prev,
				documents: [...otherDocuments, newDocument],
			};
		});
	};

	const handleFileRemove = (documentType: string) => {
		setFilesToUpload(prev => {
			const newFiles = { ...prev };
			delete newFiles[documentType];
			return newFiles;
		});
		setAdmission(prev => ({
			...prev,
			documents: prev.documents?.filter((d: any) => d.name !== documentType)
		}));
	};

	const handleEdit = (step: number) => {
		setAdmission(prev => ({ ...prev, step }));
	};

	const handleNext = async () => {
		if (!formValid()) {
			return;
		}
		setLoading(true);
		if (admission.step === steps.length - 1) {
			try {
				showAlert("Formulário enviado com sucesso!");
				setAdmission((prevAdmission) => {
					const newAdmission = {
						...prevAdmission,
						step: prevAdmission.step + 1
					};
					save(newAdmission);
					return newAdmission;
				});
			} catch (error) {
				showError("Ocorreu um erro ao enviar o formulário.");
			}
			setLoading(false);
			return;
		}
		try {
			if (admission.step === 1) {
				await sendDocuments();
			}
			setAdmission((prevAdmission) => {
				const newAdmission = {
					...prevAdmission,
					step: prevAdmission.step + 1
				};
				save(newAdmission);
				return newAdmission;
			});
		} catch (error) {
			showError("Ocorreu um erro ao salvar as informações.");
		}
		setLoading(false);
	};

	const handleClotheCmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const cmString = event.target.value;
		setClotheCm(cmString);

		const cm = parseFloat(cmString);
		if (isNaN(cm)) {
			setCalculatedClotheSize("");
			handleChange("clotheSize", "");
			return;
		}

		let size = "";
		if (cm <= 88) size = "PP";
		else if (cm >= 89 && cm <= 96) size = "P";
		else if (cm >= 97 && cm <= 104) size = "M";
		else if (cm >= 105 && cm <= 112) size = "G";
		else if (cm >= 113 && cm <= 120) size = "GG";
		else if (cm > 120) size = "XG";
		else size = "";

		handleChange("clotheSize", size);
		setCalculatedClotheSize(size);
	};

	const handleShoeCmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const cmString = event.target.value;
		setShoeCm(cmString);

		const cm = parseFloat(cmString);
		if (isNaN(cm)) {
			setCalculatedShoeSize("");
			handleChange("shoeSize", "");
			return;
		}

		let size = "";
		if (cm < 22.0) size = "";
		else if (cm >= 22.0 && cm < 22.5) size = "34";
		else if (cm >= 22.5 && cm < 23.0) size = "35";
		else if (cm >= 23.0 && cm < 23.5) size = "36";
		else if (cm >= 23.5 && cm < 24.0) size = "37";
		else if (cm >= 24.0 && cm < 25.0) size = "38";
		else if (cm >= 25.0 && cm < 25.5) size = "39";
		else if (cm >= 25.5 && cm < 26.0) size = "40";
		else if (cm >= 26.0 && cm < 26.5) size = "41";
		else if (cm >= 26.5 && cm < 27.0) size = "42";
		else if (cm >= 27.0) size = "43";
		else size = "";

		handleChange("shoeSize", size);
		setCalculatedShoeSize(size);
	};
	const handleBack = () => {
		setAdmission((prevAdmission) => ({
			...prevAdmission,
			step: prevAdmission.step - 1
		}));
	};

	useEffect(() => {
		const loadAdmission = async () => {
			try {
				const response = await service.getValidAdmission(id || "");
				if (response && response.data) {
					setAdmission(response.data);
					if (response.data.step > 0) {
						setTerms(true);
					}
					const documents = await service.getRequiredDocuments(id || "");
					if (documents && documents.data) {
						setRequiredDocuments(documents.data);
					}
				}
			} catch (error) {
				showError("Erro ao obter link de candidato");
			}
		};
		loadAdmission();
	}, []);

	if (!admission.id) {
		return (
			<div className="sem-execucoes">
				<Typography variant="h5">
					Link expirado ou não encontrado...
				</Typography>
			</div>
		);
	}

	function getStepContent() {
		switch (admission.step) {
			case 0:
				return (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Nome completo"
								variant="outlined"
								disabled
								value={admission?.name}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
								<DatePicker
									label="Data de nascimento"
									sx={{ width: '100%' }}
									disabled
									value={dayjs(admission.birthDate, "DD/MM/YYYY")}
								/>
							</LocalizationProvider>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl component="fieldset" disabled>
								<FormLabel component="legend">Sexo</FormLabel>
								<RadioGroup row name="sexo">
									<FormControlLabel value="feminino" control={<Radio checked={admission.sex === "F"}/>} label="Feminino" />
									<FormControlLabel value="masculino" control={<Radio checked={admission.sex === "M"}/>} label="Masculino" />
								</RadioGroup>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="E-mail"
								type="email"
								variant="outlined"
								value={admission.email}
								onChange={(e) => handleChange("email", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<InputMask mask="(99) 99999-9999"
								value={admission.phone}
								onChange={(e: any) => handleChange("phone", e.target.value)}
							>
								{(inputProps: any) => (
									<TextField
										{...inputProps}
										label={"Telefone (WhatsApp)"}
										variant="outlined"
										fullWidth
									/>
								)}
							</InputMask>
						</Grid>
						<Grid item xs={12}>
							<Paper elevation={3} className="lgpd-consent">
								{LGPDTerm}
								<Box sx={{ textAlign: 'center', mt: 1 }}>
									<FormControlLabel
										control={
											<Checkbox
												checked={terms}
												onChange={() => {
													setTerms(!terms);
												}}
												size="small"
											/>
										}
										label="Li e concordo com o termo de consentimento"
									/>
									<Typography variant="caption" display="block">
										Seus dados e arquivos são tratados para fins admissionais e mantidos por tempo limitado. Saiba mais na <PrivacyPolicyLink label="Política de Privacidade e LGPD" />.
									</Typography>
								</Box>
							</Paper>
						</Grid>
					</Grid>
				);
			case 1:
				return (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Paper elevation={3} sx={{ p: 2, mb: 2 }}>
								<Typography variant="body1">
									Envie fotos ou arquivos legíveis dos documentos abaixo. Tamanho máximo: 5MB por arquivo.
								</Typography>
								{!!requiredDocuments.length && (
									<Alert
										severity={missingRequiredDocuments().length ? "warning" : "success"}
										sx={{ mt: 2 }}
									>
										{missingRequiredDocuments().length
											? `Faltam ${missingRequiredDocuments().length} de ${requiredDocuments.length} documento(s) obrigatório(s) para avançar.`
											: 'Todos os documentos obrigatórios foram anexados.'}
									</Alert>
								)}
							</Paper>
							<TableContainer component={Paper}>
								<Table>
									<TableBody>
										{documentList.map((docName) => {
											const uploadedDoc = admission.documents?.find((d: any) => d.name === docName);
											return (
												<TableRow key={docName}>
													<TableCell>
														<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
															{requiredDocuments.includes(docName) && (
																<Chip label="Obrigatório" color="error" size="small" variant="outlined" />
															)}
															{docName}
														</Box>
													</TableCell>
													<TableCell align="right">
														{uploadedDoc ? (
															<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
																<Typography variant="body2" sx={{ mr: 2 }}>{uploadedDoc.originalName}</Typography>
																<IconButton onClick={() => handleFileRemove(docName)} color="error">
																	<DeleteIcon />
																</IconButton>
															</Box>
														) : (
															<Button variant="contained" component="label">
																Selecionar
																<input
																	type="file"
																	hidden
																	accept="image/*,application/pdf"
																	onChange={(e) => {
																		if (e.target.files && e.target.files[0]) {
																			handleFileSelect(docName, e.target.files[0]);
																		}
																	}}
																/>
															</Button>
														)}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					</Grid>
				);
			case 2:
				return (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography className="bold" variant="body1">Informações pessoais</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FormControl fullWidth>
								<InputLabel>Raça/Cor</InputLabel>
								<Select
									value={admission.race || ''}
									label="Raça/Cor"
									onChange={(e) => handleChange("race", e.target.value)}
								>
									{raceOptions.map(option => (
										<MenuItem key={option} value={option}>{option}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FormControl fullWidth>
								<InputLabel>Escolaridade</InputLabel>
								<Select
									value={admission.education || ''}
									label="Escolaridade"
									onChange={(e) => handleChange("education", e.target.value)}
								>
									{educationOptions.map(option => (
										<MenuItem key={option} value={option}>{option}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FormControl fullWidth>
								<InputLabel>Estado civil</InputLabel>
								<Select
									value={admission.maritalStatus || ''}
									label="Estado civil"
									onChange={(e) => handleChange("maritalStatus", e.target.value)}
								>
									{maritalStatusOptions.map(option => (
										<MenuItem key={option} value={option}>{option}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FormControl fullWidth>
								<InputLabel>Tamanho do jaleco</InputLabel>
								<Select
									value={admission.clotheSize || ''}
									label="Tamanho do jaleco"
									onChange={(e) => handleChange("clotheSize", e.target.value)}
									endAdornment={
										<InputAdornment position="end" sx={{ mr: 2 }}>
											<Tooltip title="Como medir?">
												<IconButton onClick={() => setShowClotheSizeHelper(!showClotheSizeHelper)} edge="end">
													<InfoOutlined />
												</IconButton>
											</Tooltip>
										</InputAdornment>
									}
								>
									{clotheSizeOptions.map(option => (
										<MenuItem key={option} value={option}>{option}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<TextField
								fullWidth
								label="Número do calçado"
								variant="outlined"
								type='number'
								value={admission.shoeSize || ''}
								onChange={(e) => handleChange("shoeSize", e.target.value)}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="Como medir?">
												<IconButton onClick={() => setShowShoeSizeHelper(!showShoeSizeHelper)} edge="end">
													<InfoOutlined />
												</IconButton>
											</Tooltip>
										</InputAdornment>
									)
								}}
							/>
						</Grid>
						{showClotheSizeHelper && (
							<Grid item xs={12}>
								<Paper elevation={3} sx={{ p: 2, backgroundColor: "#010610" }}>
									<Typography variant="h6" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<InfoOutlined /> Como descobrir o tamanho do seu jaleco
									</Typography>
									<Grid container spacing={2} alignItems="stretch">
										<Grid item xs={12} md={4}>
											<img src={roupas1} alt="Instruções para medir o tórax" style={{ width: '100%', display: 'block', objectFit: 'contain', height: '100%' }} />
										</Grid>
										<Grid item xs={12} md={4}>
											<img src={roupas2} alt="Tabela de referência de tamanhos" style={{ width: '100%', display: 'block', objectFit: 'contain', height: '100%' }} />
										</Grid>
										<Grid item xs={12} md={4}>
											<Typography variant="subtitle1" gutterBottom>Recomendações:</Typography>
											<Typography component="div" variant="body2" sx={{ "ul": { pl: 2, mb: 2 } }}>
												<ul>
													<li>Utilize uma fita métrica flexível.</li>
													<li>Faça a medição usando roupas leves.</li>
													<li>Não aperte a fita durante a medição.</li>
													<li>Caso fique entre dois tamanhos, escolha o maior para maior conforto durante o trabalho.</li>
												</ul>
											</Typography>
											<TextField
												label="Circunferência do tórax"
												type="number"
												value={clotheCm}
												onChange={handleClotheCmChange}
												InputProps={{
													startAdornment: <InputAdornment position="start">cm</InputAdornment>,
												}}
												sx={{ mt: 2, width: '100%' }}
											/>
											{calculatedClotheSize && (
												<Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 1, textAlign: 'center' }}>
													<Typography variant="body1">Tamanho sugerido:</Typography>
													<Typography variant="h6">{calculatedClotheSize}</Typography>
												</Box>
											)}
										</Grid>
									</Grid>
								</Paper>
							</Grid>
						)}
						{showShoeSizeHelper && (
							<Grid item xs={12}>
								<Paper elevation={3} sx={{ p: 2, backgroundColor: "#000c16" }}>
									<Typography variant="h6" component="h3" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<InfoOutlined /> Como descobrir o número do seu calçado
									</Typography>
									<Grid container spacing={2} alignItems="stretch">
										<Grid item xs={12} md={4}>
											<img src={sapato1} alt="Passo 1 e 2 para medir o pé" style={{ width: '100%', display: 'block', objectFit: 'contain', height: '100%' }} />
										</Grid>
										<Grid item xs={12} md={4}>
											<img src={sapato2} alt="Passo 3 e 4 para medir o pé" style={{ width: '100%', display: 'block', objectFit: 'contain', height: '100%' }} />
										</Grid>
										<Grid item xs={12} md={4}>
											<Typography variant="subtitle1" gutterBottom>Recomendações:</Typography>
											<Typography component="div" variant="body2" sx={{ "ul": { pl: 2, mb: 2 } }}>
												<ul>
													<li>Meça os dois pés e utilize sempre a medida do maior.</li>
													<li>Meça no final do dia.</li>
													<li>Utilize fita métrica ou régua.</li>
													<li>Caso fique entre dois números, informe o que normalmente utiliza ou o imediatamente superior.</li>
												</ul>
											</Typography>
											<TextField
												label="Medida do pé"
												type="number"
												value={shoeCm}
												onChange={handleShoeCmChange}
												InputProps={{
													startAdornment: <InputAdornment position="start">cm</InputAdornment>,
												}}
												sx={{ mt: 2, width: '100%' }}
											/>
											{calculatedShoeSize && (
												<Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 1, textAlign: 'center' }}>
													<Typography variant="body1">Tamanho sugerido:</Typography>
													<Typography variant="h6">{calculatedShoeSize}</Typography>
												</Box>
											)}
										</Grid>
									</Grid>
								</Paper>
							</Grid>
						)}
						<Grid item xs={12}>
							<Typography className="bold" variant="body1">Contato de emergência</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Nome do contato de emergência"
								variant="outlined"
								value={admission.emergencyName || ''}
								onChange={(e) => handleChange("emergencyName", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<InputMask
								mask="(99) 99999-9999"
								value={admission.emergencyPhone || ''}
								onChange={(e: any) => handleChange("emergencyPhone", e.target.value)}
							>
								{(inputProps: any) => (<TextField {...inputProps} label="Telefone do contato de emergência" variant="outlined" fullWidth />)}
							</InputMask>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth>
								<InputLabel>Grau de parentesco</InputLabel>
								<Select
									value={admission.emergencyType || ''}
									label="Grau de parentesco"
									onChange={(e) => handleChange("emergencyType", e.target.value)}
								>
									{kinshipOptions.map(option => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sx={{ mt: 2 }}>
							<Typography className="bold" variant="body1">Dependentes menores de 14 anos</Typography>
						</Grid>
						<Grid item xs={12}>
							<FormControl component="fieldset">
								<FormLabel component="legend">Possui dependentes menores de 14 anos?</FormLabel>
								<RadioGroup
									row
									name="hasChild"
									value={admission.hasChild === null || admission.hasChild === undefined ? '' : String(admission.hasChild)}
									onChange={(e) => handleChange("hasChild", e.target.value === 'true')}
								>
									<FormControlLabel value="true" control={<Radio />} label="Sim" />
									<FormControlLabel value="false" control={<Radio />} label="Não" />
								</RadioGroup>
							</FormControl>
							<Paper elevation={2} sx={{ p: 2 }}>
								<Typography variant="caption">
									Caso a resposta seja sim, os documentos dos dependentes deverão ser enviados na etapa de documentos.
								</Typography>
							</Paper>
						</Grid>
					</Grid>
				);
			case 3:
				return (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Typography variant="h5" sx={{ mb: 2 }}>Revise suas informações</Typography>
						</Grid>
						<Grid item xs={12}>
							<Paper elevation={2} sx={{ p: 2 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
									<Typography variant="h6">Dados Pessoais</Typography>
									<Button onClick={() => handleEdit(0)} startIcon={<EditIcon />}>Editar</Button>
								</Box>
								<Grid container spacing={1}>
									<Grid item xs={12} sm={6}><Typography><strong>Nome:</strong> {admission.name}</Typography></Grid>
									<Grid item xs={12} sm={6}><Typography><strong>Data de Nascimento:</strong> {admission.birthDate}</Typography></Grid>
									<Grid item xs={12} sm={6}><Typography><strong>Email:</strong> {admission.email}</Typography></Grid>
									<Grid item xs={12} sm={6}><Typography><strong>Telefone:</strong> {admission.phone}</Typography></Grid>
									<Grid item xs={12} sm={6}><Typography><strong>Sexo:</strong> {admission.sex === 'F' ? 'Feminino' : 'Masculino'}</Typography></Grid>
								</Grid>
							</Paper>
						</Grid>
						<Grid item xs={12}>
							<Paper elevation={2} sx={{ p: 2 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
									<Typography variant="h6">Documentos</Typography>
									<Button onClick={() => handleEdit(1)} startIcon={<EditIcon />}>Editar</Button>
								</Box>
								<List dense>
									{admission.documents && admission.documents.length > 0 ? admission.documents.map(doc => (
										<ListItem key={doc.name}>
											<ListItemIcon><DescriptionIcon /></ListItemIcon>
											<ListItemText primary={doc.name} secondary={doc.originalName} />
										</ListItem>
									)) : <Typography variant="body2">Nenhum documento enviado.</Typography>}
								</List>
							</Paper>
						</Grid>
						<Grid item xs={12}>
							<Paper elevation={2} sx={{ p: 2 }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
									<Typography variant="h6">Informações Adicionais</Typography>
									<Button onClick={() => handleEdit(2)} startIcon={<EditIcon />}>Editar</Button>
								</Box>
								<Grid container spacing={1}>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Raça/Cor:</strong> {admission.race}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Escolaridade:</strong> {admission.education}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Estado Civil:</strong> {admission.maritalStatus}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Tamanho do Jaleco:</strong> {admission.clotheSize}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Número do Calçado:</strong> {admission.shoeSize}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Contato de Emergência:</strong> {admission.emergencyName}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Telefone de Emergência:</strong> {admission.emergencyPhone}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Parentesco:</strong> {admission.emergencyType}</Typography></Grid>
									<Grid item xs={12} sm={6} md={4}><Typography><strong>Possui dependentes menores de 14 anos:</strong> {admission.hasChild ? 'Sim' : 'Não'}</Typography></Grid>
								</Grid>
							</Paper>
						</Grid>
						<Grid item xs={12} sx={{ mt: 2 }}>
							<Paper elevation={3} className="lgpd-consent">
								<FormControlLabel
									control={
										<Checkbox
											checked={finalConsent}
											onChange={(e) => setFinalConsent(e.target.checked)}
											size="small"
										/>
									}
									label="Declaro que as informações acima são verdadeiras e autorizo o tratamento dos meus dados pessoais para fins de processo admissional, conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)"
								/>
							</Paper>
						</Grid>
					</Grid>
				);
			case 4:
				return (
					<Box sx={{ textAlign: 'center', p: 3 }}>
						<Typography variant="h5" gutterBottom>Obrigado!</Typography>
						<Typography variant="body1">Seus dados foram enviados com sucesso.</Typography>
					</Box>
				);
		}
	}

	return (
		<div className="audit-admission-form-page">
			{admission.name && <Container component={Paper} className="form-container">
				<Typography variant="h4" component="h1" className="form-title">
					Portal do Candidato
				</Typography>
				<Stepper activeStep={admission.step} sx={{ mb: 4 }}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
				<Box className="form-content">
					{getStepContent()}
				</Box>
				{admission.step < steps.length && (
					<Box className="form-actions">
						<Button
							disabled={admission.step === 0}
							onClick={handleBack}
							startIcon={<ArrowBack />}
						>
							Voltar
						</Button>
						<Button
							variant="contained"
							onClick={handleNext}
							color="success"
							disabled={!formValid() || loading}
						>
							{admission.step === steps.length - 1
								? 'Enviar'
								: loading
									? <CircularProgress sx={{color: "black"}} size={'25px'}/>
									: 'Avançar'
							}
						</Button>
					</Box>
				)}
			</Container>}
		</div>
	);

	function formValid() {
		switch (admission.step) {
			case 0:
				return !!admission.email && !!admission.phone && terms;
			case 1:
				if (requiredDocuments.length > 0) {
					return missingRequiredDocuments().length === 0;
				}
				return admission.documents && (admission.documents as any[]).length > 0;
			case 2:
				return !!admission.race &&
					!!admission.education &&
					!!admission.maritalStatus &&
					!!admission.clotheSize &&
					!!admission.shoeSize &&
					!!admission.emergencyName &&
					!!admission.emergencyPhone &&
					!!admission.emergencyType &&
					admission.hasChild !== null &&
					admission.hasChild !== undefined;
			case 3:
				return finalConsent;
		}
	}

	async function sendDocuments() {
		const files = Object.entries(filesToUpload);
		if (files.length === 0) {
			return;
		}
		const formData = new FormData();
		files.forEach(([documentType, file]) => {
			formData.append('files', file, `${documentType}___${file.name}`);
		});
		try {
			await service.sendDocuments(id!, formData);
			setFilesToUpload({});
		} catch (error) {
			showError("Erro ao enviar documentos.");
			throw error;
		}
	}

	async function save(data: AuditAdmissionDTO) {
		const { latitude, longitude, locationName } = await getLocation();
		await service.saveAdmission(data, latitude, longitude, locationName);
	}
};

export default AuditAdmissionForm;